import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState } from 'react';
import { alpha, Box, Button, FormHelperText, Typography, useTheme } from '@mui/material';
import { FileUploader } from 'react-drag-drop-files';
import HFilledTextField from '../common/TextField/HFilledTextField';
import { FileErrorType } from '@application/enums/file-error-type';
import { useFileUploaderStyles } from './useFileUploaderSyles';
import { useTranslation } from 'react-i18next';
import { EventDocumentType } from '@application/enums/event-document-type';
import SingleSelect from '@ui/components/common/Autocomplete/SingleSelect';
import { useForm, useWatch } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { CollaboratorAccessLevel } from '@application/enums/collaborator-access-level';
export default function FileUploaderForm({ onChange, onFilesSubmitted, onValidationError, allowedFileTypes, allowedFileTypesText, allowedFileSize, error, infoText, showSubmitButton = true, disabled = false, isFamilyFest = false }) {
    const { t } = useTranslation();
    const theme = useTheme();
    const styles = useFileUploaderStyles(theme);
    const { control, getValues } = useForm({
        defaultValues: {
            attachmentType: null
        }
    });
    const watchedAttachmentType = useWatch({ control, name: 'attachmentType' });
    const [title, setTitle] = useState('');
    const [file, setFile] = useState(null);
    const [errorText, setErrorText] = useState('');
    const [canUpload, setCanUpload] = useState(true);
    const [titleError, setTitleError] = useState('');
    const [fileError, setFileError] = useState('');
    const [attachmentTypeError, setAttachmentTypeError] = useState('');
    useEffect(() => {
        if (watchedAttachmentType !== null &&
            watchedAttachmentType !== undefined &&
            attachmentTypeError) {
            setAttachmentTypeError('');
        }
    }, [watchedAttachmentType, attachmentTypeError]);
    const currentUserRoles = useSelector((state) => state.auth.currentUser?.role);
    const collaboratorAccessLevel = useSelector((state) => state.auth.currentUser?.CollaboratorAccessLevel);
    const isSfeermeester = Array.isArray(currentUserRoles) && currentUserRoles.includes('Sfeermeester');
    const isCollaboratorOrTransport = Array.isArray(currentUserRoles) &&
        (currentUserRoles.includes('Collaborator') || currentUserRoles.includes('TransportManager'));
    const isExternal = Number(collaboratorAccessLevel) === CollaboratorAccessLevel.External;
    const attachmentTypeOptions = isFamilyFest
        ? (() => {
            if (isSfeermeester)
                return [{ label: t('events.spheresMaster') || 'Sfeermeester', value: EventDocumentType.SpheresMaster }];
            if (isCollaboratorOrTransport)
                return isExternal
                    ? [{ label: t('events.externalSupplier') || 'External Supplier', value: EventDocumentType.External }]
                    : [{ label: t('events.internalSupplier') || 'Internal Supplier', value: EventDocumentType.Internal }];
            return [
                { label: t('events.all') || 'All', value: EventDocumentType.All },
                { label: t('events.internalSupplier') || 'Internal Supplier', value: EventDocumentType.Internal },
                { label: t('events.externalSupplier') || 'External Supplier', value: EventDocumentType.External },
                { label: t('events.spheresMaster') || 'Sfeermeester', value: EventDocumentType.SpheresMaster },
                { label: t('events.happinessManager') || 'Happiness Manager', value: EventDocumentType.HappinessManager },
            ];
        })()
        : [
            { label: t('events.internal') || 'Internal', value: EventDocumentType.Internal },
            { label: t('events.external') || 'External', value: EventDocumentType.External }
        ];
    useEffect(() => {
        if (error)
            setErrorText(error);
    }, [error]);
    const checkFileSize = (file) => allowedFileSize && file.size / 1024 / 1024 > allowedFileSize;
    const fileTypeError = () => {
        setErrorText(`File type error. Please upload ${allowedFileTypesText} files only.`);
        onValidationError?.(true, FileErrorType.TypeError);
        setCanUpload(true);
    };
    const fileSizeError = () => {
        setErrorText(`File size error. Maximum file size is ${allowedFileSize}MB.`);
        onValidationError?.(true, FileErrorType.SizeError);
        setCanUpload(true);
    };
    const handleFileChange = (selected) => {
        const uploadedFile = Array.isArray(selected) ? selected[0] : selected;
        if (!uploadedFile)
            return;
        const fileExtension = uploadedFile.name.split('.').pop()?.toLowerCase();
        const isValidType = !!fileExtension && allowedFileTypes.includes(fileExtension);
        if (!isValidType)
            return fileTypeError();
        if (checkFileSize(uploadedFile))
            return fileSizeError();
        setFile(uploadedFile);
        setErrorText('');
        setFileError('');
        setCanUpload(true);
        onValidationError?.(false, FileErrorType.NoError);
        onChange?.([uploadedFile], title.trim());
    };
    const handleSubmit = () => {
        let hasError = false;
        if (!title.trim()) {
            setTitleError(t('fileUpload.titleRequired') || 'Title is required.');
            hasError = true;
        }
        else {
            setTitleError('');
        }
        if (!file) {
            setFileError(t('fileUpload.fileRequired') || 'Please upload a file.');
            hasError = true;
        }
        else {
            setFileError('');
        }
        const selectedType = getValues('attachmentType');
        if (selectedType === null || selectedType === undefined) {
            setAttachmentTypeError(t('fileUpload.accessLevelRequired') || 'Access level is required.');
            hasError = true;
        }
        else {
            setAttachmentTypeError('');
        }
        if (hasError)
            return;
        onFilesSubmitted?.([file], title.trim(), selectedType);
        setFile(null);
        setTitle('');
        setTitleError('');
        setFileError('');
    };
    const handleDragEnter = (e) => {
        e.preventDefault();
        e.stopPropagation();
    };
    const handleDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
    };
    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
    };
    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (disabled || !canUpload)
            return;
        const droppedFiles = e.dataTransfer.files;
        if (droppedFiles && droppedFiles.length > 0) {
            handleFileChange(droppedFiles[0]);
        }
    };
    return (_jsxs(Box, { component: 'form', sx: styles.container, noValidate: true, onSubmit: (e) => e.preventDefault(), children: [_jsx(Box, { sx: {
                    color: theme.palette.text.primary,
                    whiteSpace: 'nowrap',
                    bgcolor: alpha(theme.palette.background.paper, 0.92),
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    borderRadius: theme.spacing(1.5),
                    px: theme.spacing(1.5),
                    py: theme.spacing(1.5)
                }, children: _jsx(Typography, { variant: 'h5', children: t('events.eventDetails.attachmentsTabs.addNewAttachment') }) }), _jsx(HFilledTextField, { label: t('fileUpload.title'), value: title, bg: 'grey8', onChange: (e) => {
                    setTitle(e.target.value);
                    if (titleError)
                        setTitleError('');
                }, error: Boolean(titleError), helperText: titleError }), _jsx(SingleSelect, { control: control, name: 'attachmentType', bg: 'grey8', label: t('fileUpload.accessLevel'), options: attachmentTypeOptions, error: attachmentTypeError }), _jsx(FileUploader, { id: 'fileInput', multiple: false, handleChange: handleFileChange, types: allowedFileTypes, disabled: disabled || !canUpload, error: error, required: true, onTypeError: fileTypeError, onSizeError: fileSizeError, children: _jsx(Box, { sx: styles.dropZone, children: _jsx(Box, { onDragEnter: handleDragEnter, onDragLeave: handleDragLeave, onDragOver: handleDragOver, onDrop: handleDrop, sx: { width: '100%' }, children: _jsxs(Box, { sx: styles.uploadTextBox, children: [_jsx(Typography, { variant: 'h6', sx: styles.uploadTitle, children: t('fileUpload.uploadTitle') }), _jsxs(Typography, { variant: 'body2', sx: styles.uploadSubtitle, children: [t('fileUpload.dropInstruction.dropFiles'), ' ', _jsx("span", { style: { color: theme.palette.primary.main }, children: t('fileUpload.dropInstruction.browse') }), ' ', t('fileUpload.dropInstruction.throughYourMachine')] })] }) }) }) }), (fileError || errorText) && (_jsx(FormHelperText, { error: true, sx: { mt: 0 }, children: fileError || errorText })), file && _jsxs(Typography, { variant: 'body2', children: ["\uD83D\uDCCE ", file.name] }), showSubmitButton && (_jsx(Button, { size: 'large', variant: 'contained', color: 'primary', onClick: handleSubmit, disabled: disabled, children: t('fileUpload.addAttachments') }))] }));
}
